<template>
  <main
    class="page"
    role="main"
  >
    <header
      class="page-header"
      aria-labelledby="title"
    >
      <h1
        id="title"
        class="h1"
      >
        Angebote – Tagesplan
      </h1>
    </header>

    <section class="card">
      <div
        class="toolbar"
        style="display:flex; gap:12px; align-items:flex-end; flex-wrap:wrap;"
      >
        <div
          class="field date-picker"
          style="width: 155px;"
        >
          <label for="ov-date">Datum</label>
          <div style="display:flex;align-items:center;gap:4px;">
            <button
              type="button"
              class="icon-btn"
              :title="'Vorheriger Tag'"
              :aria-label="'Vorheriger Tag'"
              @click="prevDay"
            >
              <i
                class="pi pi-angle-left"
                aria-hidden="true"
              />
            </button>
            <flat-pickr
              id="ov-date"
              v-model="date"
              :config="fpOpts"
              class="input"
              @on-change="load"
            />
            <button
              type="button"
              class="icon-btn"
              :title="'Nächster Tag'"
              :aria-label="'Nächster Tag'"
              @click="nextDay"
            >
              <i
                class="pi pi-angle-right"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div
          v-if="grouping !== 'teacher'"
          class="field"
          style="min-width:200px; flex:0 0 200px;"
        >
          <span class="label">Klassen</span>

          <div
            class="class-dropdown"
            @keydown.esc.prevent="classesOpen = false"
          >
            <button
              id="ov-classes-btn"
              type="button"
              class="input class-dropdown-trigger"
              :aria-label="'Klassen filtern'"
              :aria-expanded="classesOpen ? 'true' : 'false'"
              aria-controls="ov-classes-panel"
              @click="toggleClassesDropdown"
              @keydown.enter.prevent="toggleClassesDropdown"
              @keydown.space.prevent="toggleClassesDropdown"
            >
              <span class="class-dropdown-trigger-text">
                {{ selectedClassesLabel }}
              </span>
              <i
                class="pi"
                :class="classesOpen ? 'pi-chevron-up' : 'pi-chevron-down'"
                aria-hidden="true"
              />
            </button>

            <div
              v-show="classesOpen"
              id="ov-classes-panel"
              class="class-dropdown-panel"
              role="listbox"
              :aria-labelledby="'ov-classes-btn'"
            >
              <ul class="class-dropdown-list">
                <li
                  v-for="c in availableClasses"
                  :key="c"
                  class="class-dropdown-item"
                  :class="{'is-checked': selectedClasses.includes(c)}"
                  role="option"
                  :aria-selected="selectedClasses.includes(c) ? 'true' : 'false'"
                >
                  <label class="class-dropdown-option">
                    <input
                      v-model="selectedClasses"
                      type="checkbox"
                      class="class-dropdown-checkbox"
                      :value="c"
                    >
                    <span class="class-dropdown-label">{{ c }}</span>
                  </label>
                </li>

                <li
                  v-if="!availableClasses.length"
                  class="class-dropdown-empty"
                >
                  <span class="muted">Keine Klassen verfügbar.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- NEU: Timeline-Filter neben dem Klassen-Dropdown -->
        <div
          v-if="grouping === 'student' || grouping === 'studentPresence'"
          class="field"
          style="min-width:220px; flex:0 0 220px;"
        >
          <span class="label">Timeline-Filter</span>

          <div
            class="timeline-filter-dropdown"
            @keydown.esc.prevent="timelineFilterOpen = false"
          >
            <button
              id="ov-timeline-filter-btn"
              type="button"
              class="input class-dropdown-trigger"
              :aria-label="'Timeline filtern'"
              :aria-expanded="timelineFilterOpen ? 'true' : 'false'"
              aria-controls="ov-timeline-filter-panel"
              @click="toggleTimelineFilterDropdown"
              @keydown.enter.prevent="toggleTimelineFilterDropdown"
              @keydown.space.prevent="toggleTimelineFilterDropdown"
            >
              <span class="class-dropdown-trigger-text">
                {{ timelineFilterLabel }}
              </span>
              <i
                class="pi"
                :class="timelineFilterOpen ? 'pi-chevron-up' : 'pi-chevron-down'"
                aria-hidden="true"
              />
            </button>

            <div
              v-show="timelineFilterOpen"
              id="ov-timeline-filter-panel"
              class="class-dropdown-panel"
              role="listbox"
              :aria-labelledby="'ov-timeline-filter-btn'"
            >
              <ul class="class-dropdown-list">
                <li
                  v-for="opt in timelineFilterOptions"
                  :key="opt.value"
                  class="class-dropdown-item"
                  :class="{'is-checked': timelineFilter === opt.value}"
                  role="option"
                  :aria-selected="timelineFilter === opt.value ? 'true' : 'false'"
                >
                  <button
                    type="button"
                    class="timeline-filter-option-btn"
                    @click="selectTimelineFilter(opt.value)"
                  >
                    {{ opt.label }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <label
          class="field"
          style="min-width:220px; flex:1 1 240px;"
        >
          <span class="label">Suche</span>
          <input
            id="ov-search"
            v-model.trim="searchQuery"
            type="search"
            class="input"
            placeholder="Lehrer oder Schüler"
            autocomplete="off"
          >
        </label>

        <div class="field">
          <span class="label">Ansicht</span>
          <div
            class="segmented"
            role="tablist"
            aria-label="Ansicht wechseln"
          >
            <button
              type="button"
              class="segmented-btn"
              :class="{ active: grouping === 'teacher' }"
              role="tab"
              :aria-selected="grouping === 'teacher' ? 'true' : 'false'"
              @click="grouping = 'teacher'"
            >
              Lehrer
            </button>
            <button
              type="button"
              class="segmented-btn"
              :class="{ active: grouping === 'student' }"
              role="tab"
              :aria-selected="grouping === 'student' ? 'true' : 'false'"
              @click="grouping = 'student'"
            >
              Schüler Buchungen
            </button>
            <button
              type="button"
              class="segmented-btn"
              :class="{ active: grouping === 'studentPresence' }"
              role="tab"
              :aria-selected="grouping === 'studentPresence' ? 'true' : 'false'"
              @click="grouping = 'studentPresence'"
            >
              Schüler Präsenz
            </button>
          </div>
        </div>

        <button
          class="btn btn-primary"
          type="button"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>

      <div class="timelines">
        <EventTimeline
          :rows="groupedRows"
          :day="date"
          :start-time="dayStartTime"
          :end-time="dayEndTime"
          :mode="timelineMode"
          :grace-minutes="presenceGraceMinutes"
          :can-select="canSelectEvent"
          @select="onSelect"
          @student="onStudentInfo"
          @checkin="onRowCheckin"
          @signout="onRowSignout"
        />
      </div>

      <EventAttendanceDialog
        :open="!!selectedEvent"
        :event-id="selectedEvent?.id"
        :event-teacher-first-name="selectedEvent?.teacher_first_name"
        :event-teacher-last-name="selectedEvent?.teacher_last_name"
        :event-status="selectedEvent?.status"
        :event-title="selectedEvent?.subject || selectedEvent?.topic || ''"
        :event-mandatory="Boolean(selectedEvent?.isMandatory ?? selectedEvent?.is_mandatory)"
        :event-starts-at="selectedEvent?.startsAt || selectedEvent?.starts_at"
        :event-ends-at="selectedEvent?.endsAt || selectedEvent?.ends_at"
        :initial-search-term="selectedStudentLabel"
        @close="selectedEvent = null; selectedEventId = null; selectedStudentLabel = ''"
        @updated="load"
      />

      <StudentDayTimelineDialog
        :open="!!selectedStudentRow && (grouping === 'student' || grouping === 'studentPresence')"
        :student-name="selectedStudentRow?.label || ''"
        :date="date"
        :events="selectedStudentRow?.events || []"
        :student-id="selectedStudentRow?.studentId || selectedStudentRow?.student_id || null"
        :grace-minutes="presenceGraceMinutes"
        :accepted-notification-subject="selectedStudentRow?.hasNotification ? (selectedStudentRow?.notifySubject || '') : ''"
        @close="selectedStudentRow = null"
        @saved="onStaySaved"
      />

      <StudentReleaseConfirmDialog
        :open="releaseConfirmOpen"
        :student="releaseRow"
        :occurred-at="releaseAtIso"
        :stay-id="releaseRow?.stayId ?? null"
        @close="closeReleaseDialog"
        @released="load"
      />

      <StudentArriveConfirmDialog
        :open="arriveConfirmOpen"
        :student-name="arriveStudentName"
        @close="closeArriveDialog"
        @confirm="confirmArrive"
      />

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>
    </section>

    <!-- Top-Navigation: Hilfe-Button -->
    <teleport to=".top-nav">
      <span
        id="top-nav-help-offers-day"
        class="top-nav-help"
      >
        <UiInfoButton title="Hilfe: Tagesübersicht">
          <ul>
            <li>Datum wählen, optional nach Lehrkraft/Schüler suchen.</li>
            <li>Ansicht umschalten: Gruppierung nach Lehrer oder Schüler.</li>
            <li>Klick auf ein Angebot öffnet die Anwesenheitsverwaltung.</li>
            <li>In der Schüleransicht zeigt der Klick auf den Namen die Tagesübersicht des Schülers.</li>
            <li>„Reset“ setzt Suche zurück und stellt die Standardansicht wieder her.</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {onMounted, onBeforeUnmount, ref, computed, watch} from 'vue';
import EventTimeline from '@/components/common/EventTimeline.vue';
import EventAttendanceDialog from '@/components/events/EventAttendanceDialog.vue';
import StudentDayTimelineDialog from '@/components/events/StudentDayTimelineDialog.vue';
import StudentReleaseConfirmDialog from '@/components/dialogs/StudentReleaseConfirmDialog.vue';
import StudentArriveConfirmDialog from '@/components/dialogs/StudentArriveConfirmDialog.vue';
import {eventsApi} from '@/api/events';
import {schoolSettingsApi} from '@/api/schoolSettings';
import {presenceApi} from '@/api/presence';
import {useAuthStore} from '@/stores/auth';
import {useRoute} from 'vue-router';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';
import UiInfoButton from '@/components/UiInfoButton.vue';
import {useToast} from 'primevue/usetoast';
import {fetchActiveStudents} from '@/api/students';

// NEU: alle aktiven Schüler (für "alle Schüler" Timeline-Filter in student/studentPresence)
const activeStudents = ref([]);

// NEU: Cache/Loader-State (einmalig laden)
const activeStudentsLoaded = ref(false);
const activeStudentsLoading = ref(false);

async function ensureActiveStudentsLoaded() {
  if (activeStudentsLoaded.value) return;
  if (activeStudentsLoading.value) return;

  activeStudentsLoading.value = true;
  try {
    activeStudents.value = await fetchActiveStudents(); // /students/all -> { items: [...] } -> Array
    activeStudentsLoaded.value = true;
  } finally {
    activeStudentsLoading.value = false;
  }
}

// flatpickr Optionen
const fpOpts = {
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d',
  locale: German,
  disableMobile: true
};

const date = ref(new Date().toISOString().slice(0, 10));

// Filter & Gruppierung
const searchQuery = ref('');
const selectedClasses = ref([]); // Multiselect: Array<string>
const grouping = ref('teacher'); // 'teacher' | 'student' | 'studentPresence'

// NEU: Timeline-Filter (Single Select)
const timelineFilterOpen = ref(false);
const timelineFilter = ref('withBookings'); // 'all' | 'withBookings' | 'noArrival' | 'arrived' | 'released' | 'notice' | 'emptyArrival' | 'arrivledWithAbsence'

const timelineFilterOptions = [
  {value: 'withBookings', label: 'mit Buchungen'},
  {value: 'all', label: 'alle Schüler'},
  {value: 'noArrival', label: 'noch keine Ankunft'},
  {value: 'arrived', label: 'noch in der Schule'},
  {value: 'released', label: 'Entlassen'},
  {value: 'notice', label: 'mit Meldung'},
  {value: 'arrivledWithAbsence', label: 'mit Fehlzeiten'},
  {value: 'emptyArrival', label: 'leerer Aufenthalt'}
];

const timelineFilterLabel = computed(() => {
  const found = timelineFilterOptions.find(o => o.value === timelineFilter.value);
  return found ? found.label : 'Alle Schüler';
});

function toggleTimelineFilterDropdown() {
  timelineFilterOpen.value = !timelineFilterOpen.value;
}

function selectTimelineFilter(val) {
  timelineFilter.value = val;
  timelineFilterOpen.value = false;
}

const selectedClassesLabel = computed(() => {
  const sel = Array.isArray(selectedClasses.value) ? selectedClasses.value : [];
  if (!sel.length) return 'Alle Klassen';
  if (sel.length === 1) return sel[0];
  return `${sel.length} Klassen ausgewählt`;
});

function toggleClassesDropdown() {
  classesOpen.value = !classesOpen.value;
}

// Dropdowns bei Klick außerhalb schließen
function onDocClick(ev) {
  const classesRoot = document.querySelector('.class-dropdown');
  if (classesRoot && !classesRoot.contains(ev.target)) {
    classesOpen.value = false;
  }

  const tlRoot = document.querySelector('.timeline-filter-dropdown');
  if (tlRoot && !tlRoot.contains(ev.target)) {
    timelineFilterOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
});

// Timeline-Modus (wichtig für EventTimeline)
// - teacher -> 'teacher'
// - student -> 'student'
// - studentPresence -> 'student-present'
const timelineMode = computed(() => {
  if (grouping.value === 'teacher') return 'teacher';
  if (grouping.value === 'studentPresence') return 'student-present';
  return 'student';
});

// Feste Tagesgrenzen: 07:00–17:00
const dayStartTime = '07:00:00';
const dayEndTime = '17:00:00';

const events = ref([]);
const settings = ref({dayStartTime: dayStartTime, dayEndTime: dayEndTime, gapToleranceMinutes: 15});
const error = ref(null);

const selectedEvent = ref(null);
const selectedStudentRow = ref(null);
const selectedStudentLabel = ref('');
const selectedEventId = ref(null);

// Dropdown-State + Anzeige-Label
const classesOpen = ref(false);

onMounted(() => {
  document.addEventListener('click', onDocClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
});

// ---- Timeline-Filter: Hilfsfunktionen (Row-Zustände) ----
function rowHasNotification(row) {
  return Boolean(row?.hasNotification);
}

function rowIsCurrentlyInSchool(row) {
  if (!row) return false;
  // Primär: hasOpenStay vom Backend (heute angekommen, noch nicht entlassen)
  if (typeof row.hasOpenStay === 'boolean') {
    return row.hasOpenStay;
  }
  // Fallback: in stays nach „arrived && !released“ suchen
  const stays = Array.isArray(row.stays) ? row.stays : [];
  return stays.some(st => {
    const arrived = Boolean(st?.arrivedAt || st?.arrived_at);
    const released = Boolean(st?.releasedAt || st?.released_at);
    return arrived && !released;
  });
}

function rowHasAnyArrival(row) {
  if (!row) return false;
  if (row.arrivalAt) return true;

  const stays = Array.isArray(row.stays) ? row.stays : [];
  return stays.some(st => Boolean(st?.arrivedAt || st?.arrived_at));
}

function rowWasReleased(row) {
  if (!row) return false;
  if (row.lastReleaseAt) return true;

  const stays = Array.isArray(row.stays) ? row.stays : [];
  return stays.some(st => Boolean(st?.releasedAt || st?.released_at));
}

function rowHasAttendanceCheckin(row) {
  const events = Array.isArray(row?.events) ? row.events : [];
  const readDate = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (!v) continue;
      const d = new Date(v);
      if (!isNaN(d)) return d;
    }
    return null;
  };

  const hasCheckinInExtras = (ev) => {
    const candidates = [];
    if (Array.isArray(ev?.attendanceExtras)) candidates.push(...ev.attendanceExtras);
    if (Array.isArray(ev?.attendance_extras)) candidates.push(...ev.attendance_extras);
    if (Array.isArray(ev?.extras)) candidates.push(...ev.extras);
    if (Array.isArray(ev?.extraAttendances)) candidates.push(...ev.extraAttendances);
    if (Array.isArray(ev?.blocks)) candidates.push(...ev.blocks);

    for (const b of candidates) {
      const ci = readDate(b, ['checkinAt', 'checkin_at', 'checkedInAt', 'checked_in_at']);
      if (ci) return true;
    }
    return false;
  };

  for (const ev of events) {
    const rootCi = readDate(ev, ['checkinAt', 'checkin_at', 'checkin', 'checkedInAt', 'checked_in_at']);
    if (rootCi) return true;
    if (hasCheckinInExtras(ev)) return true;
  }
  return false;
}

// NEU: Alle Check-in Zeitpunkte aus Events/Extras extrahieren (als Date[])
function rowGetAttendanceCheckinDates(row) {
  const events = Array.isArray(row?.events) ? row.events : [];

  const readDate = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (!v) continue;
      const d = new Date(v);
      if (!isNaN(d)) return d;
    }
    return null;
  };

  const out = [];

  for (const ev of events) {
    const rootCi = readDate(ev, ['checkinAt', 'checkin_at', 'checkin', 'checkedInAt', 'checked_in_at']);
    if (rootCi) out.push(rootCi);

    const candidates = [];
    if (Array.isArray(ev?.attendanceExtras)) candidates.push(...ev.attendanceExtras);
    if (Array.isArray(ev?.attendance_extras)) candidates.push(...ev.attendance_extras);
    if (Array.isArray(ev?.extras)) candidates.push(...ev.extras);
    if (Array.isArray(ev?.extraAttendances)) candidates.push(...ev.extraAttendances);
    if (Array.isArray(ev?.blocks)) candidates.push(...ev.blocks);

    for (const b of candidates) {
      const ci = readDate(b, ['checkinAt', 'checkin_at', 'checkedInAt', 'checked_in_at']);
      if (ci) out.push(ci);
    }
  }

  return out;
}

// NEU: true, sobald es mind. einen Stay mit Ankunft gibt, der keinen Check-in im Stay-Zeitfenster hat
function rowHasAnyStayWithoutAttendanceCheckin(row) {
  if (!row) return false;

  const stays = Array.isArray(row.stays) ? row.stays : [];
  const checkins = rowGetAttendanceCheckinDates(row);

  // Wenn es keine Stays gibt, können wir nicht stay-basiert prüfen -> Fallback auf bisherige Row-Logik
  if (!stays.length) {
    return rowHasAnyArrival(row) && !rowHasAttendanceCheckin(row);
  }

  const toDateOrNull = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? null : d;
  };

  for (const st of stays) {
    const arrived = toDateOrNull(st?.arrivedAt || st?.arrived_at);
    if (!arrived) continue; // nur Stays mit Ankunft sind relevant

    const released = toDateOrNull(st?.releasedAt || st?.released_at);
    const startMs = arrived.getTime();
    const endMs = released ? released.getTime() : Number.POSITIVE_INFINITY;

    const hasCiInWindow = checkins.some(d => {
      const t = d.getTime();
      return t >= startMs && t <= endMs;
    });

    if (!hasCiInWindow) {
      return true; // dieser Stay ist "leer" -> Row ist emptyArrival
    }
  }

  return false;
}

function rowHasAbsenceWithArrival(row) {
  // Backend liefert hasAbsences direkt
  // Kombination: angekommen (hasOpenStay) UND hat Fehlzeiten (hasAbsences)
  return Boolean(row?.arrivalAt) && Boolean(row?.hasAbsences);
}

function rowMatchesTimelineFilter(row) {
  const f = timelineFilter.value;

  if (f === 'all') return true;

  if (f === 'withBookings') {
    // Zeigt nur aktive Schüler-Zeilen, die für den Tag mind. eine Angebotsbuchung haben.
    // (Voraussetzung: Backend liefert in 'all' auch Schüler ohne Buchungen als Row mit events: [])
    const evs = Array.isArray(row?.events) ? row.events : [];
    return evs.length > 0;
  }

  if (f === 'noArrival') {
    // Soll sich nur auf Schüler MIT Buchungen/Angeboten beziehen (nicht auf alle aktiven Schüler)
    const evs = Array.isArray(row?.events) ? row.events : [];
    const hasBookings = evs.length > 0;
    return hasBookings && !rowHasAnyArrival(row);
  }

  if (f === 'arrived') {
    // In der Schule: angekommen, aber noch nicht entlassen
    return rowIsCurrentlyInSchool(row);
  }

  if (f === 'arrivledWithAbsence') {
    // Angekommen UND hat Fehlzeiten (mindestens ein Event ohne Checkin)
    return rowHasAbsenceWithArrival(row);
  }

  if (f === 'released') {
    return rowWasReleased(row);
  }

  if (f === 'notice') {
    return rowHasNotification(row);
  }

  if (f === 'emptyArrival') {
    // Wichtig: alle Stays prüfen (es reicht, wenn EIN Stay ohne Check-in existiert)
    return rowHasAnyArrival(row) && rowHasAnyStayWithoutAttendanceCheckin(row);
  }

  return true;
}

// Ankunfts-Dialog (Timeline-Checkin)
const arriveConfirmOpen = ref(false);
const arriveRow = ref(null);
const arriveStudentName = ref('');
const toast = useToast();

// Entlassungs-Dialog (Timeline-Checkout)
const releaseConfirmOpen = ref(false);
const releaseRow = ref(null);
const releaseAtIso = ref('');

// Auth-Store für Nutzer-/Rolleninfos
const auth = useAuthStore();

// Robuste Ermittlung der Lehrer-/Besitzer-ID eines Events
function getTeacherId(ev) {
  if (!ev || typeof ev !== 'object') return null;

  const tryKeys = [
    'teacherId',
    'teacher_id',
    'teacherUserId',
    'teacher_user_id',
    'ownerId',
    'owner_id'
  ];

  for (const k of tryKeys) {
    const v = ev[k];
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }

  const t = ev.teacher || ev.owner || null;
  if (t && typeof t === 'object') {
    const cand = t.id ?? t.userId ?? t.user_id;
    const n = Number(cand);
    if (Number.isFinite(n)) return n;
  }

  return null;
}

function isOwnOffer(ev) {
  const me = Number(auth.user?.id);
  const tid = getTeacherId(ev);
  return Number.isFinite(me) && Number.isFinite(tid) && me === tid;
}

function canSelectEvent(ev) {
  if (ev.status === "canceled") {
    return false;
  }

  const roleId = auth.user?.roleId;
  // Lehrer: nur eigene Angebote anklickbar
  if (roleId === 2) {
    return isOwnOffer(ev);
  }
  // Admin (und ggf. andere erlaubte Rollen): alles anklickbar
  return true;
}

function nameFromStudentRecord(s) {
  if (!s || typeof s !== 'object') return '';
  const first = String(s.first_name || '').trim();
  const last = String(s.last_name || '').trim();
  return `${first} ${last}`.trim();
}

function getClassKeysFromStudent(s) {
  if (!s || typeof s !== 'object') return [];
  const lvl = Number(s.class_level);
  if (!Number.isFinite(lvl) || lvl <= 0) return [];
  return [`Klasse ${lvl}`];
}

async function load() {
  error.value = null;
  try {
    const needsStudents = (grouping.value === 'student' || grouping.value === 'studentPresence');
    if (needsStudents) {
      await ensureActiveStudentsLoaded();
    }

    const [evRes, setRes] = await Promise.all([
      eventsApi.getDayGrouped({
        date: date.value,
        groupBy: (grouping.value === 'studentPresence' ? 'student' : grouping.value),
        teacherId: 'all'
      }),
      schoolSettingsApi.getCurrent()
    ]);

    events.value = evRes.data || [];
    if (setRes?.data) settings.value = setRes.data;
  } catch (e) {
    error.value = 'Laden fehlgeschlagen.';
  }
}

function shiftDay(delta) {
  if (!date.value) {
    date.value = new Date().toISOString().slice(0, 10);
  }
  const d = new Date(date.value);
  if (Number.isNaN(d.getTime())) {
    date.value = new Date().toISOString().slice(0, 10);
  } else {
    d.setDate(d.getDate() + delta);
    date.value = d.toISOString().slice(0, 10);
  }
}

function prevDay() {
  shiftDay(-1);
}

function nextDay() {
  shiftDay(1);
}

// Ermittelt robuste numerische Event-ID, auch aus IDs mit Block-Suffix (z. B. "42-b1")
function resolveEventId(ev) {
  if (!ev || typeof ev !== 'object') return null;

  const toNumeric = (v) => {
    if (v === null || v === undefined) return null;
    const direct = Number(v);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const s = String(v).trim();
    const m = s.match(/^(\d+)/); // Präfix-Zahl z. B. "42-b1" -> 42
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  const keys = [
    'eventId',
    'event_id',
    'offerEventId',
    'offer_event_id',
    'offerId',
    'offer_id',
    '_id',
    'id'
  ];

  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(ev, k)) {
      const n = toNumeric(ev[k]);
      if (n) return n;
    }
  }
  return null;
}

function onSelect(ev, studentLabel) {
  // In beiden Ansichten öffnen; in der Schüleransicht den Namen vorfiltern
  selectedEvent.value = ev || null;
  selectedEventId.value = ev ? resolveEventId(ev) : null;

  if (grouping.value === 'student' || grouping.value === 'studentPresence') {
    // studentLabel kommt aus EventTimeline (row.label)
    selectedStudentLabel.value = String(studentLabel || '').trim();
  } else {
    selectedStudentLabel.value = '';
  }
}

// Timeline-Checkin klick: Dialog öffnen
function onRowCheckin(row) {
  arriveRow.value = row || null;
  arriveStudentName.value = String(row?.label || '').trim();
  arriveConfirmOpen.value = true;
}

function closeArriveDialog() {
  arriveConfirmOpen.value = false;
  arriveRow.value = null;
  arriveStudentName.value = '';
}

function onRowSignout(row) {
  releaseRow.value = row || null;
  releaseAtIso.value = new Date().toISOString();
  releaseConfirmOpen.value = true;
}

function closeReleaseDialog() {
  releaseConfirmOpen.value = false;
  releaseRow.value = null;
  releaseAtIso.value = '';
}

// Student-ID aus Row ableiten
function getStudentIdFromRow(row) {
  if (!row) return null;
  // 1) Direkt an der Row gesetzte ID (durch groupedRows)
  const directCandidates = [row.studentId, row.student_id];
  for (const v of directCandidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }

  return null;
}

async function confirmArrive(payload) {
  if (!arriveRow.value) return;

  const studentId = getStudentIdFromRow(arriveRow.value);
  if (!studentId) {
    toast.add({severity: 'error', summary: 'Ungültige Schüler-ID.', life: 3000});
    return;
  }

  // occurredAt aus dem Dialog übernehmen; Fallback: now()
  let when = payload && typeof payload.occurredAt === 'string' && payload.occurredAt
      ? payload.occurredAt
      : new Date().toISOString();

  try {
    await presenceApi.in({
      studentId,
      occurredAt: when
    });
    toast.add({
      severity: 'success',
      summary: `${arriveStudentName.value || 'Schüler'} ist angekommen.`,
      life: 3000
    });
    closeArriveDialog();
    load();
  } catch (e) {
    const msg = e?.response?.data?.error?.message || 'Ankunft konnte nicht gespeichert werden.';
    toast.add({severity: 'error', summary: msg, life: 8000});
  }
}

// Wird aufgerufen, wenn im Tagesbericht ein Stay gespeichert wurde
function onStaySaved() {
  // getDayGrouped erneut laden → studentPresence-Lanes (Ankommen/Entlassungen) neu berechnen
  load();
}

watch(grouping, () => {
  selectedEvent.value = null;
  selectedEventId.value = null;
  selectedStudentRow.value = null;
  selectedStudentLabel.value = '';
  classesOpen.value = false;
  timelineFilterOpen.value = false;

  load();
});

// Reset-Button setzt Suche zurück und stellt Gruppierung auf Standard
function resetFilters() {
  searchQuery.value = '';
  selectedClasses.value = [];
  classesOpen.value = false;

  // Reset-Default: in Schüler-Ansichten "mit Buchungen"
  timelineFilter.value = (grouping.value === 'student' || grouping.value === 'studentPresence')
    ? 'withBookings'
    : 'all';

  timelineFilterOpen.value = false;
}

function normalizeClassLabel(v) {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  if (!s) return '';
  // kleine Normalisierung, ohne aggressive Umformungen
  return s.replace(/\s+/g, ' ');
}

// Klasseninfos aus Event robust ableiten (ohne GUI-Ausgabe; nur für Filter)
function getClassKeysFromEvent(e) {
  if (!e || typeof e !== 'object') return [];

  const out = [];

  const direct = [
    e.groupName, e.group_name,
    e.studentGroup, e.student_group,
    e.className, e.class_name,
    e.class, e.group
  ];
  for (const v of direct) {
    const lbl = normalizeClassLabel(v);
    if (lbl) out.push(lbl);
  }

  // ggf. innerhalb student/participants (falls Backend so liefert)
  const studentObj = e.student && typeof e.student === 'object' ? e.student : null;
  if (studentObj) {
    const lbl = normalizeClassLabel(
        studentObj.groupName ?? studentObj.group_name ??
        studentObj.studentGroup ?? studentObj.student_group ??
        studentObj.className ?? studentObj.class_name ??
        studentObj.class ?? studentObj.group ??
        studentObj.classLevel ?? studentObj.class_level
    );
    if (lbl) out.push(lbl);
  }

  // fallback: class_level (numerisch) → als "Klasse X" ausgeben
  const lvl = e.classLevel ?? e.class_level ?? null;
  const n = Number(lvl);
  if (Number.isFinite(n) && n > 0) out.push(`Klasse ${n}`);

  return Array.from(new Set(out));
}

const availableClasses = computed(() => {
  const list = [];
  for (const r of (groupedRowsRaw.value || [])) {
    const keys = Array.isArray(r.classKeys) ? r.classKeys : [];
    for (const k of keys) {
      const lbl = normalizeClassLabel(k);
      if (lbl) list.push(lbl);
    }
  }

  const unique = Array.from(new Set(list));

  // Zahlensichere Sortierung: zuerst nach erster vorkommender Zahl, dann nach Rest
  unique.sort((a, b) => {
    const toParts = (s) => {
      const str = String(s ?? '').trim();
      const m = str.match(/(\d+)/);
      if (m) {
        const num = Number(m[1]);
        return {hasNum: true, num, rest: str.toLowerCase()};
      }
      return {hasNum: false, rest: str.toLowerCase()};
    };

    const A = toParts(a);
    const B = toParts(b);

    if (A.hasNum && B.hasNum) {
      if (A.num !== B.num) return A.num - B.num;
      return A.rest.localeCompare(B.rest, 'de', {sensitivity: 'base'});
    }

    // Fallback: alphabetisch
    return String(a).localeCompare(String(b), 'de', {sensitivity: 'base'});
  });

  return unique;
});

// Hilfsfunktionen zur Namensextraktion (robust gegenüber unterschiedlichen Strukturen)
function nameFromParts(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const full = obj.fullName || obj.fullname || obj.name;
  if (full) return String(full);
  const fn = obj.firstName || obj.firstname || obj.givenName || '';
  const ln = obj.lastName || obj.lastname || obj.familyName || '';
  return [fn, ln].filter(Boolean).join(' ').trim();
}

function getTeacherName(e) {
  if (!e) return '';
  return (
      e.teacherName ||
      e.teacher_name ||
      nameFromParts(e.teacher) ||
      e.teacherFullName ||
      ''
  );
}

// Student-ID robust aus einem Event-Objekt ziehen
function getStudentIdFromEvent(e) {
  if (!e || typeof e !== 'object') return null;

  const directKeys = ['studentId', 'student_id'];
  for (const k of directKeys) {
    const v = e[k];
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }

  return null;
}

function getStudentNames(e) {
  const out = [];
  if (!e) return out;
  // Häufige Felder
  if (Array.isArray(e.students)) {
    for (const s of e.students) {
      if (typeof s === 'string') out.push(s);
      else out.push(nameFromParts(s));
    }
  }
  if (e.studentName || e.student_name) out.push(e.studentName || e.student_name);
  if (e.student) out.push(nameFromParts(e.student));
  if (Array.isArray(e.participants)) {
    for (const p of e.participants) {
      // optional: nach Rolle filtern, falls vorhanden
      const role = (p.role || p.type || '').toString().toLowerCase();
      if (!role || role.includes('student') || role.includes('schuel') || role.includes('schüler')) {
        out.push(typeof p === 'string' ? p : nameFromParts(p));
      }
    }
  }
  if (Array.isArray(e.attendees)) {
    for (const a of e.attendees) {
      const role = (a.role || a.type || '').toString().toLowerCase();
      if (!role || role.includes('student') || role.includes('schuel') || role.includes('schüler')) {
        out.push(typeof a === 'string' ? a : nameFromParts(a));
      }
    }
  }
  // Bereinigen
  return Array.from(new Set(out.map(s => String(s || '').trim()).filter(Boolean)));
}

// groupedRows in 2 Stufen: erst Rohdaten + classKeys, dann Filter (Suche + Klassen)
const groupedRowsRaw = computed(() => {
  const raw = Array.isArray(events.value) ? events.value : [];
  // Falls Backend bereits gruppiert liefert: [{ label, events, key? }, ...]
  if (raw.length && typeof raw[0] === 'object' && Array.isArray(raw[0].events) && 'label' in raw[0]) {
    let rows = raw.map(r => {
      const evs = Array.isArray(r.events) ? r.events : [];
      const sid = r.studentId ?? r.student_id ?? null;

      const classFromRow = normalizeClassLabel(
          r.groupName ?? r.group_name ??
          r.studentGroup ?? r.student_group ??
          r.className ?? r.class_name ??
          r.class ?? r.group ??
          (Number.isFinite(Number(r.classLevel ?? r.class_level)) ? `Klasse ${Number(r.classLevel ?? r.class_level)}` : '')
      );
      const classFromEvents = evs.flatMap(getClassKeysFromEvent);
      const classKeys = Array.from(new Set([classFromRow, ...classFromEvents].map(normalizeClassLabel).filter(Boolean)));

      // Stay-ID aus dem Backend-Payload robust ziehen
      const stayIdRaw = r.stayId ?? null;
      const stayIdNum = Number(stayIdRaw);
      const stayId = Number.isFinite(stayIdNum) && stayIdNum > 0 ? stayIdNum : null;

      return {
        key: r.key || ((grouping.value === 'teacher' ? 't:' : 's:') + String(r.label || '').toLowerCase()),
        label: String(r.label || ''),
        notifySubject: r.notifySubject ?? r.notify_subject ?? '',
        hasNotification: Boolean(r.hasNotification ?? r.notifyAccepted ?? false),
        events: evs,
        // Präsenzdaten aus student_school_stays
        hasOpenStay: Boolean(r.hasOpenStay ?? false),
        arrivalAt: r.arrivalAt ?? null,
        lastReleaseAt: r.lastReleaseAt ?? null,
        // vollständige Stays-Liste für den Tag
        stays: Array.isArray(r.stays) ? r.stays : [],
        // ID der Anmeldung (student_school_stays.id)
        stayId: stayId,
        studentId: sid,
        student_id: sid,
        // NEU: Fehlzeiten-Flag vom Backend
        hasAbsences: Boolean(r.hasAbsences ?? false),

        // Klassen am Schüler "anhaften" (für Filter; nicht in der GUI ausgeben)
        classKeys: classKeys
      };
    });

    if (grouping.value === 'student' || grouping.value === 'studentPresence') {
      const base = new Map();

      for (const s of (Array.isArray(activeStudents.value) ? activeStudents.value : [])) {
        const sid = Number(s?.id);
        if (!Number.isFinite(sid) || sid <= 0) continue;

        const label = nameFromStudentRecord(s) || String(sid);

        base.set(sid, {
          key: 's:' + String(sid),
          label,
          notifySubject: '',
          hasNotification: false,
          events: [],
          hasOpenStay: false,
          arrivalAt: null,
          lastReleaseAt: null,
          stays: [],
          stayId: null,
          studentId: sid,
          student_id: sid,
          hasAbsences: false,
          classKeys: getClassKeysFromStudent(s)
        });
      }

      // Backend-Rows in Base mergen (per studentId; Fallback via Label)
      for (const r of rows) {
        const sid = Number(r?.studentId ?? r?.student_id);
        if (Number.isFinite(sid) && sid > 0 && base.has(sid)) {
          const t = base.get(sid);
          t.events = Array.isArray(r.events) ? r.events : [];
          t.hasNotification = Boolean(r.hasNotification ?? r.notifyAccepted ?? t.hasNotification);
          t.notifySubject = (r.notifySubject ?? r.notify_subject ?? t.notifySubject) || '';
          t.hasOpenStay = Boolean(r.hasOpenStay ?? t.hasOpenStay);
          t.arrivalAt = r.arrivalAt ?? t.arrivalAt ?? null;
          t.lastReleaseAt = r.lastReleaseAt ?? t.lastReleaseAt ?? null;
          t.stays = Array.isArray(r.stays) ? r.stays : (Array.isArray(t.stays) ? t.stays : []);
          t.stayId = r.stayId ?? t.stayId ?? null;
          t.hasAbsences = Boolean(r.hasAbsences ?? t.hasAbsences);

          const merged = new Set([...(t.classKeys || []), ...((r.classKeys || []))].map(normalizeClassLabel).filter(Boolean));
          t.classKeys = Array.from(merged);
          continue;
        }

        // Fallback: per Label matchen (falls studentId im Payload fehlt)
        const lbl = String(r?.label || '').trim().toLowerCase();
        if (!lbl) continue;
        for (const t of base.values()) {
          if (String(t.label || '').trim().toLowerCase() === lbl) {
            t.events = Array.isArray(r.events) ? r.events : [];
            t.hasNotification = Boolean(r.hasNotification ?? r.notifyAccepted ?? t.hasNotification);
            t.notifySubject = (r.notifySubject ?? r.notify_subject ?? t.notifySubject) || '';
            t.hasOpenStay = Boolean(r.hasOpenStay ?? t.hasOpenStay);
            t.arrivalAt = r.arrivalAt ?? t.arrivalAt ?? null;
            t.lastReleaseAt = r.lastReleaseAt ?? t.lastReleaseAt ?? null;
            t.stays = Array.isArray(r.stays) ? r.stays : (Array.isArray(t.stays) ? t.stays : []);
            t.stayId = r.stayId ?? t.stayId ?? null;
            t.hasAbsences = Boolean(r.hasAbsences ?? t.hasAbsences);

            const merged = new Set([...(t.classKeys || []), ...((r.classKeys || []))].map(normalizeClassLabel).filter(Boolean));
            t.classKeys = Array.from(merged);
            break;
          }
        }
      }

      rows = Array.from(base.values()).sort((a, b) => a.label.localeCompare(b.label, 'de'));
      return rows;
    }





    rows = rows.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    return rows;
  }

  // Fallback: Frontend-Gruppierung
  const map = new Map();
  for (const ev of raw) {
    if (grouping.value === 'teacher') {
      const label = (getTeacherName(ev) || 'Unbekannter Lehrer').trim();
      const key = 't:' + label.toLowerCase();
      if (!map.has(key)) map.set(key, {key, label, events: [], classKeys: []});
      map.get(key).events.push(ev);
    } else {
      const students = getStudentNames(ev);
      const names = (students.length ? students : ['Unbekannter Schüler']).map(s => s.trim()).filter(Boolean);
      const evStudentId = getStudentIdFromEvent(ev);
      const evClassKeys = getClassKeysFromEvent(ev);

      for (const name of names) {
        const key = 's:' + name.toLowerCase();
        const hasNotice = Boolean(ev.hasNotification || ev.notifyAccepted);
        const subject = String(ev.notifySubject || '');

        if (!map.has(key)) {
          map.set(key, {
            key,
            label: name,
            notifySubject: subject,
            hasNotification: hasNotice,
            events: [],
            stayId: null,
            studentId: evStudentId ?? null,
            student_id: evStudentId ?? null,
            classKeys: []
          });
        }

        const row = map.get(key);

        // Klassen sammeln (nur anhängen, nicht anzeigen)
        const merged = new Set([...(row.classKeys || []), ...evClassKeys]);
        row.classKeys = Array.from(merged);

        // Falls die Row noch keine ID hat, jetzt setzen
        if (!row.studentId && evStudentId) {
          row.studentId = evStudentId;
          row.student_id = evStudentId;
        }
        if (hasNotice) {
          row.hasNotification = row.hasNotification || true;
          if (!row.notifySubject && subject) row.notifySubject = subject;
        }
        row.events.push(ev);
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'de'));
});

const groupedRows = computed(() => {
  let rows = groupedRowsRaw.value || [];

  // Suche
  const q = searchQuery.value.trim().toLowerCase();
  if (q) rows = rows.filter(r => String(r.label || '').toLowerCase().includes(q));

  // Klassenfilter in den Ansichten 'student' und 'studentPresence' anwenden
  const sel = Array.isArray(selectedClasses.value) ? selectedClasses.value.map(normalizeClassLabel).filter(Boolean) : [];
  if (sel.length && (grouping.value === 'student' || grouping.value === 'studentPresence')) {
    const selSet = new Set(sel);
    rows = rows.filter(r => {
      const keys = Array.isArray(r.classKeys) ? r.classKeys.map(normalizeClassLabel).filter(Boolean) : [];
      return keys.some(k => selSet.has(k));
    });
  }

  // NEU: Timeline-Filter (nur Schüler-Ansichten)
  if (grouping.value === 'student' || grouping.value === 'studentPresence') {
    rows = rows.filter(rowMatchesTimelineFilter);
  }

  return rows;
});

function onStudentInfo(row) {
  if (grouping.value !== 'student' && grouping.value !== 'studentPresence') {
    return;
  }
  selectedStudentRow.value = row || null;
}

const presenceGraceMinutes = computed(() => {
  const s = settings.value || {};
  const v = s.presence_grace_minutes ?? s.presenceGraceMinutes ?? s.lateGraceMinutesDefault ?? 5;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 5;
});

const route = useRoute();

function applyFiltersFromRoute() {
  const q = route?.query || {};

  const g = String(q.group || q.view || '').toLowerCase();

  if (g === 'studentpresence') {
    grouping.value = 'studentPresence';
    timelineFilter.value = 'all';
  } else if (g === 'student') {
    grouping.value = 'student';
    timelineFilter.value = 'withBookings';
  }

  const tl = q.timelineFilter ?? q.timeline ?? null;
  if (typeof tl === 'string' && tl) {
    timelineFilter.value = tl;
  }

  const s = q.search || q.q || '';
  if (s) {
    searchQuery.value = String(s);
  }

  const qDate = q.date ?? null;
  if (typeof qDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(qDate)) {
    date.value = qDate;
  }
}

onMounted(() => {
  applyFiltersFromRoute();
  load();
});

// Hilfe-Button vor .profile einsortieren
onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-offers-day');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-offers-day');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});
</script>

<style scoped>
.page {
  padding: 0;
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.h1 {
  font-size: 26px;
  line-height: 38px;
  font-weight: 600;
  margin: 0;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 0;
  height: calc(100vh - 160px);
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 0
}

.label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 500;
}

.input {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.input:focus {
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
  outline: none;
}

/* Klassen Dropdown */
.class-dropdown {
  position: relative;
}

/* Timeline-Filter Dropdown Container: für korrekt positioniertes Panel */
.timeline-filter-dropdown {
  position: relative;
}

.class-dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  width: 100%;
}

.class-dropdown-trigger-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-dropdown-panel {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  max-height: 305px;
  overflow: auto;
}

.class-dropdown-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.class-dropdown-item {
  border-radius: 6px;
}

.class-dropdown-item:hover {
  background: rgba(79, 70, 229, 0.06);
}

/* Sichtbare Markierung ausgewählter Optionen */
.class-dropdown-item.is-checked {
  background: rgba(79, 70, 229, 0.10);
}

.class-dropdown-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  cursor: pointer;
  user-select: none;
  width: 100%;
}

.class-dropdown-checkbox {
  width: 16px;
  height: 16px;
}

.class-dropdown-label {
  flex: 1;
  min-width: 0;
  font-weight: 500;
}

.class-dropdown-empty {
  padding: 8px 10px;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
}

.notice-danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: #FCA5A5;
}

.mt-12 {
  margin-top: 12px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Segmented Control */
.segmented {
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--surface);
  white-space: nowrap;
}

.segmented-btn:first-child {
  border: 0;
}

.segmented-btn {
  appearance: none;
  border: 0;
  border-left: 1px solid #ddd;
  background: transparent;
  color: var(--text);
  padding: 8px 0;
  cursor: pointer;
  line-height: 20px;
  width: 150px;
}

.segmented-btn:hover {
  background: rgba(79, 70, 229, 0.06);
}

.segmented-btn.active {
  background: var(--primary);
  color: #fff;
}

.segmented-btn:focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 2px;
}

/* Zeilenlayout Container */
.timelines {
  width: 100%;
}

/* Layout-Fix: Viewport <1200px → fixiere Layout-Spalten auf 240px + 1200px */
@media (max-width: 1199px) {
  :global(.layout) {
    grid-template-columns: 240px 1200px !important;
  }
}

/* Grundstil für die kleinen Pfeil-Buttons neben dem Datepicker */
.date-picker .icon-btn {
  height: 30px;
  width: 30px;
  border-radius: 5px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* WICHTIG: Container-Hover neutralisieren, damit nicht beide Buttons reagieren */
.date-picker:hover .icon-btn {
  background: var(--surface); /* kein gemeinsamer Hover-Effekt */
}

/* Nur der tatsächlich gehoverte Button bekommt einen Hover-Hintergrund */
.date-picker .icon-btn:hover {
  background: var(--surface-100);
}

/* Icon selbst soll keine eigenen Pointer-Events haben */
.date-picker .icon-btn i {
  pointer-events: none;
}

/* NEU: Timeline-Filter Option-Button im Dropdown (nutzt vorhandene Tokens) */
.timeline-filter-option-btn {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 10px;
  cursor: pointer;
  border-radius: 6px;
  color: var(--text);
}

.timeline-filter-option-btn:hover {
  background: rgba(79, 70, 229, 0.06);
}
</style>

